<script>
import ProspectContactList from '@components/prospects/prospect-contact-list.vue';
import ProspectMessageComposerHeader from '@components/prospects/prospect-message-composer-header.vue';
import { mixin as clickaway } from 'vue-clickaway';
import formatPhoneNumber from '@utils/format-phone-number';
import {
  adminPanelComputed,
  prospectComputed,
  prospectMethods,
  campaignComputed,
  campaignMethods,
  authComputed,
  adminPanelMethods,
  alertMethods,
  modalMethods,
  organizationComputed,
} from '@state/helpers';

export default {
  components: {
    ProspectContactList,
    ProspectMessageComposerHeader,
  },
  mixins: [clickaway],
  data() {
    return {
      isFormValid: true,
      isSending: false,
      showConnOptsDropdown: false,
      formatPhoneNumber: formatPhoneNumber,
    };
  },
  computed: {
    ...authComputed,
    ...adminPanelComputed,
    ...campaignComputed,
    ...prospectComputed,
    ...organizationComputed,
    activeProspects() {
      return this.allProspects?.filter(
        (prospect) =>
          !this.hasReceivedCampaign(prospect) &&
          !this.isNotReadyForCampaign(prospect)
      );
    },
    outReachedProspects() {
      return this.allProspects?.filter((prospect) =>
        this.hasReceivedCampaign(prospect)
      );
    },
    inactiveProspects() {
      return this.allProspects?.filter((prospect) =>
        this.isNotReadyForCampaign(prospect)
      );
    },
    isEmailSelected() {
      return this.selectedProspect?.selectedCommsOpt === 'email';
    },
    isSmsSelected() {
      return this.selectedProspect?.selectedCommsOpt === 'phone';
    },
    phoneOpts() {
      return this.selectedProspect?.connectionOptions?.filter(
        (opt) => opt.type === 'phone'
      );
    },
    emailAddressOpts() {
      return this.selectedProspect?.connectionOptions?.filter(
        (opt) => opt.type === 'email'
      );
    },
    selectedConnOptValue() {
      return (
        this.selectedProspect?.connectionOptions?.find((opt) =>
          this.isEmailSelected
            ? opt.type === 'email' && opt.isPreferred
            : opt.type === 'phone' && opt.isPreferred
        )?.value || this.preferredContactOptFallback
      );
    },
    preferredContactOptFallback() {
      return this.isEmailSelected
        ? this.emailAddressOpts[0]?.value
        : this.phoneOpts[0]?.value;
    },
    showSmsIntro() {
      return this.isSmsSelected && this.selectedCampaign.order === 1;
    },
  },
  methods: {
    ...adminPanelMethods,
    ...alertMethods,
    ...campaignMethods,
    ...modalMethods,
    ...prospectMethods,
    toggleConnOptsDropdown() {
      this.showConnOptsDropdown = !this.showConnOptsDropdown;
    },
    formatSmsIntro() {
      return `Hey! It's ${this.trainerFullName} messaging you from my ${this.organizationTitle} platform!`;
    },
    async selectPreferredConnOpt(opt, prospect) {
      this.showConnOptsDropdown = false;
      await this.setSelectedProspectPreferredConnOpt({ opt, prospect });
      this.updateProspect({ prospect: this.selectedProspect });
    },
    hasReceivedCampaign(prospect) {
      return prospect?.status >= this.selectedCampaign?.order;
    },
    isNotReadyForCampaign(prospect) {
      const status = prospect?.status;
      return status + 1 < this.selectedCampaign?.order;
    },
    msgBodyValue() {
      if (this.isEmailSelected) {
        return this.selectedProspect?.message
          ? this.selectedProspect?.message.emailBody
          : this.selectedCampaign.emailBody;
      } else {
        return this.selectedProspect?.message
          ? this.selectedProspect?.message.smsMessage
          : this.selectedCampaign.smsMessage;
      }
    },
    buildEmailMsg() {
      const { emailSubject, emailBody } = this.selectedProspect?.message
        ? this.selectedProspect?.message
        : this.selectedCampaign;
      return { emailSubject, emailBody };
    },
    buildSmsMsg() {
      const { smsMessage } = this.selectedProspect?.message
        ? this.selectedProspect?.message
        : this.selectedCampaign;
      return { smsMessage };
    },
    updateCampaignMsgSubject(value) {
      const { emailBody } = this.buildEmailMsg();
      this.setOverriddenMultiCampaignTemplate({
        emailSubject: value,
        emailBody,
      });
      this.setUpdateRecipientMsg({
        emailSubject: value,
        emailBody,
        prospect: this.selectedProspect,
      });
    },
    updateCampaignMsgBody(value, template) {
      let msg;
      if (this.isEmailSelected) {
        const { emailSubject } = this.buildEmailMsg();
        msg = { ...this.selectedCampaign, emailSubject, emailBody: value };
      } else {
        msg = { ...this.selectedCampaign, smsMessage: value };
      }
      this.setOverriddenMultiCampaignTemplate(msg);
      this.setUpdateRecipientMsg({ msg, prospect: this.selectedProspect });
    },
    selectCommsOpt(type, prospect) {
      if (prospect?.selectedCommsOpt && prospect?.selectedCommsOpt === type) {
        this.setRemoveCampaignRecipient(prospect);
        this.setProspectSelectedCommsOpt({ type: null, prospect });
      } else if (prospect?.selectedCommsOpt) {
        this.setProspectSelectedCommsOpt({ type, prospect });
        this.setRecipientSelectedCommsOpt({ type, prospect });
      } else {
        this.setAddCampaignRecipient({ type, prospect });
        this.setProspectSelectedCommsOpt({ type, prospect });
        this.setRecipientSelectedCommsOpt({ type, prospect });
      }
    },
    discardChanges() {
      this.overriddenCampaignTemplate
        ? this.$bvModal.show('unsaved-changes-modal')
        : this.setHideAdminPanel();
    },
    handleSendSuccess(msg) {
      this.setHideAdminPanel();
      this.setAlertText(msg);
      this.setShowAlert();
    },
    handleSendError(errors) {
      const msg = errors
        .map((error) => error.message)
        .join()
        .replace(/,/g, ', ');
      this.setNotifyModalContext({
        textMain: `Uh oh!`,
        textSecond: `${msg}`,
      });
      this.$bvModal.show('notify-modal');
    },
    buildMsgPayload() {
      return this.campaignRecipients.map((recipient) => {
        const msg = {
          _id: recipient._id,
          messageType: recipient.selectedCommsOpt,
        };
        if (recipient.selectedCommsOpt === 'email') {
          msg.subject = this.buildEmailMsg().emailSubject;
          msg.message = this.buildEmailMsg().emailBody;
        } else {
          msg.message =
            this.selectedCampaign.order === 1
              ? `${this.formatSmsIntro()} ${this.buildSmsMsg().smsMessage}`
              : this.buildSmsMsg().smsMessage;
        }
        return msg;
      });
    },
    onSend() {
      this.isSending = true;
      const prospects = this.buildMsgPayload();
      this.contactProspects({
        campaignId: this.selectedCampaign._id,
        prospects,
      })
        .then((res) => {
          if (res) {
            if (res.errors.length) {
              this.handleSendError(res.errors);
            } else {
              this.handleSendSuccess(res.message);
            }
          }
          this.isSending = false;
        })
        .catch((err) => {
          this.handleSendError([
            { message: 'Something went wrong. Please try again!' },
          ]);
          this.isSending = false;
          console.warn(err);
        });
    },
  },
  watch: {
    selectedCampaign(newVal, oldVal) {
      this.setSelectedProspect(this.activeProspects[0]);
    },
    async isAdminPanelActive(newVal, oldVal) {
      if (!newVal && oldVal) {
        this.setSelectedProspect(null);
        this.setSelectedCampaign(null);
        this.setOverriddenCampaignTemplate(null);
        this.setUpdateRecipientCount(0);
        await this.setClearCampaignRecipients();
        this.setPanelContent(null);
      } else {
        const firstProspect =
          this.activeProspects[0] ||
          this.outReachedProspects[0] ||
          this.inactiveProspects[0];
        await this.setSelectedProspect(firstProspect);
        const hasPhone = await this.selectedProspect.connectionOptions.some(
          (opt) => opt.type === 'phone'
        );
        await this.setProspectSelectedCommsOpt({
          type: hasPhone ? 'phone' : 'email',
          prospect: this.selectedProspect,
        });
        this.setAddCampaignRecipient({
          type: hasPhone ? 'phone' : 'email',
          prospect: this.selectedProspect,
        });
      }
    },
  },
};
</script>

<template>
  <div class="prospect-msg-composer">
    <ProspectMessageComposerHeader
      v-if="selectedCampaign"
      :isSending="isSending"
      @send-campaign="onSend"
    />

    <div class="msg-composer-wrapper">
      <div class="msg-composer-left">
        <ProspectContactList :prospects="activeProspects" />
        <div
          v-if="selectedCampaign"
          :class="[
            'composer-prospect-list-divider',
            { 'no-prospects': !outReachedProspects.length },
          ]"
        >
          Contacts that Recieved
          {{ selectedCampaign && selectedCampaign.name }} Before
        </div>
        <ProspectContactList :prospects="outReachedProspects" />
        <div
          :class="[
            'composer-prospect-list-divider',
            { 'no-prospects': !inactiveProspects.length },
          ]"
        >
          Contacts Not Ready For Campaign
        </div>
        <ProspectContactList :prospects="inactiveProspects" />
      </div>
      <div class="msg-composer-right">
        <div class="composer-contact-form" v-if="selectedCampaign">
          <div class="composer-contact-form-header">
            <div class="contact-form-header-content">
              <div class="selected-prospect-details" v-if="selectedProspect">
                <img
                  class="avatar"
                  src="@assets/images/placeholder-avatar.png"
                />
                <div>
                  <h5 class="prospect-name" v-if="selectedProspect"
                    >{{ selectedProspect.firstName }}
                    {{ selectedProspect.lastName }}</h5
                  >
                  <div
                    class="contact-options"
                    v-if="
                      (isEmailSelected && emailAddressOpts.length) ||
                        (isSmsSelected && phoneOpts.length)
                    "
                  >
                    <span @click="toggleConnOptsDropdown">
                      <span v-if="isEmailSelected">{{
                        selectedConnOptValue
                      }}</span>
                      <span v-if="isSmsSelected && selectedConnOptValue">
                        {{ formatPhoneNumber(selectedConnOptValue) }}
                      </span>
                      <span>
                        <BaseIcon
                          v-if="selectedConnOptValue"
                          class="icon-caret"
                          :name="
                            showConnOptsDropdown ? 'caret-up' : 'caret-down'
                          "
                        />
                      </span>
                    </span>

                    <div
                      class="contact-options-dropdown"
                      v-if="showConnOptsDropdown"
                      v-on-clickaway="toggleConnOptsDropdown"
                    >
                      <ul>
                        <span v-if="isEmailSelected">
                          <li
                            class="contact-options-item"
                            v-for="(opt, i) in emailAddressOpts"
                            :key="i"
                            @click="
                              selectPreferredConnOpt(opt, selectedProspect)
                            "
                          >
                            <img
                              class="icon-star"
                              src="@assets/images/icon-star.svg"
                              v-if="selectedConnOptValue === opt.value"
                            />
                            {{ opt.value }}
                          </li>
                        </span>
                        <span v-if="isSmsSelected">
                          <li
                            class="contact-options-item"
                            v-for="(opt, i) in phoneOpts"
                            :key="i"
                            @click="
                              selectPreferredConnOpt(opt, selectedProspect)
                            "
                          >
                            <img
                              class="icon-star"
                              src="@assets/images/icon-star.svg"
                              v-if="selectedConnOptValue === opt.value"
                            />
                            {{ formatPhoneNumber(opt.value) }}
                          </li>
                        </span>
                      </ul>
                    </div>
                  </div>
                </div>
              </div>

              <div
                class="received-campaign"
                v-if="hasReceivedCampaign(selectedProspect)"
              >
                Already Recieved Campaign
              </div>
            </div>
          </div>

          <div
            class="contact-form-fields-empty"
            v-if="!isEmailSelected && !isSmsSelected"
          >
            <h3 v-if="selectedProspect">
              Select a contact option to send a <br />
              message to {{ selectedProspect.firstName }}
              {{ selectedProspect.lastName }}
            </h3>
            <div class="btn-group">
              <BaseButton
                type="button"
                class="btn-primary btn-round"
                @click="selectCommsOpt('phone', selectedProspect)"
              >
                Text
              </BaseButton>
              <BaseButton
                type="button"
                class="btn-primary btn-round"
                @click="selectCommsOpt('email', selectedProspect)"
              >
                Email
              </BaseButton>
            </div>
          </div>

          <div class="contact-form-fields" v-else>
            <h3>
              <span v-if="isEmailSelected">Edit Email</span>
              <span v-else>Edit Text Message</span>
            </h3>
            <label v-if="isEmailSelected">Subject</label>
            <BaseInputText
              v-if="isEmailSelected"
              :value="
                selectedProspect.message
                  ? selectedProspect.message.emailSubject
                  : selectedCampaign.emailSubject
              "
              @input="updateCampaignMsgSubject($event.target.value)"
            />

            <label>
              <span v-if="isEmailSelected">Message</span>
              <span v-else>Message to Send</span>
            </label>
            <p v-if="showSmsIntro">{{ formatSmsIntro() }}</p>
            <BaseInputTextarea
              type="textarea"
              rows="12"
              :value="msgBodyValue()"
              @input="updateCampaignMsgBody($event.target.value)"
            />
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@design';

.prospect-msg-composer {
  .msg-composer-wrapper {
    display: flex;
    height: 100vh;

    .msg-composer-left,
    .msg-composer-right {
      margin-top: 6.2rem;
    }

    .msg-composer-left {
      border-right: 4px solid $color-light-gray-alt;
      flex: 0.75;
    }

    .msg-composer-right {
      flex: 1;
    }

    .composer-prospect-list-divider {
      background-color: $color-light-gray-twenty-one;
      font-size: 1.2rem;
      font-weight: bold;
      letter-spacing: -0.257143px;
      line-height: 2.4rem;
      padding: 0.2rem 2.4rem;

      &.no-prospects {
        margin-bottom: 5.6rem;
      }
    }

    .composer-contact-form-header {
      padding: 1.5rem 1.5rem 1rem;

      .contact-form-header-content {
        align-items: center;
        display: flex;
        justify-content: space-between;
      }
    }

    .contact-form-fields,
    .contact-form-fields-empty {
      padding: 1.5rem;
    }

    .contact-form-fields-empty {
      align-items: center;
      background-color: $color-off-white;
      border-radius: $border-radius;
      display: flex;
      flex-direction: column;
      margin: 4rem auto 0;
      padding: 3rem;
      text-align: center;
      width: 90%;

      h3 {
        line-height: 1.6;
        margin-bottom: 2.7rem;
      }

      .btn-group {
        display: flex;
        justify-content: space-between;
        width: 17rem;
      }
    }

    // PROSPECT

    .selected-prospect-details {
      align-items: center;
      color: $color-heading-text;
      display: flex;
      flex: 1.5;
      font-size: 2.1rem;
      font-weight: 900;
      letter-spacing: -0.3px;
      line-height: 2.4rem;

      .avatar {
        border-radius: $border-radius-circle;
        height: 6.1rem;
        margin-right: 1.6rem;
        width: 6.1rem;
      }

      .contact-options {
        font-size: 1.4rem;
        font-style: normal;
        font-weight: 300;
        letter-spacing: -0.257143px;
        line-height: 1.4rem;
        position: relative;
        text-align: center;

        &:hover {
          cursor: pointer;
        }

        .contact-options-dropdown {
          background: white;
          border-radius: $base-border-radius-md;
          box-shadow: 0 0 0.4rem rgba($color-black-wo-rgb, 0.15);
          min-width: 18rem;
          padding: 1rem 0 0.8rem;
          position: absolute;
          top: 2.7rem;
          z-index: 1;

          &:hover {
            cursor: default;
          }

          .contact-options-item {
            align-items: center;
            color: black;
            display: flex;
            font-size: 1.4rem;
            font-weight: 300;
            letter-spacing: -0.257143px;
            line-height: 2.4rem;
            margin: 0.6rem 0;
            padding: 0 1.8rem;

            &:hover {
              background-color: $color-primary;
              color: white;
              cursor: pointer;
            }

            .icon-star {
              height: 1.6rem;
              margin-right: 0.7rem;
              width: 1.6rem;
            }
          }
        }
      }

      .icon-caret {
        margin-left: 1rem;
      }
    }

    .received-campaign {
      background-color: $color-yellow-pale;
      border-radius: $base-border-radius;
      color: $color-golden-grass;
      font-size: 1.4rem;
      font-weight: 800;
      letter-spacing: -0.3px;
      line-height: 2.4rem;
      padding: 0.4rem 1.5rem;
      text-align: center;
    }
  }
}
</style>
